export interface PreparedImage {
  file: File;
  thumbnail: File;
  width: number;
  height: number;
  previewUrl: string;
}

const acceptedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);

export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!acceptedTypes.has(file.type)) throw new Error("Choose a JPG, PNG or WebP image.");
  if (file.size > 20 * 1024 * 1024) throw new Error("Images must be 20 MB or smaller.");

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 960 / bitmap.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) {
    bitmap.close();
    throw new Error("This browser could not prepare the image preview.");
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const width = bitmap.width;
  const height = bitmap.height;
  bitmap.close();
  const thumbnailBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not create the image preview."))), "image/webp", 0.82);
  });
  return {
    file,
    thumbnail: new File([thumbnailBlob], "thumbnail.webp", { type: "image/webp" }),
    width,
    height,
    previewUrl: URL.createObjectURL(file),
  };
}
